import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CreateOrUpdateDialogProps {
    open: boolean;
    itemId: string | undefined;
    initialValue: string | undefined;
    valueValidator: (value: string, id?: string) => string | null;
    labels: {
        title: string;
        value: string;
    };
    onSave: (id?: string, value?: string) => void;
    onClose: () => void;
}

export type CreateOrUpdateValueValidator = Exclude<
    CreateOrUpdateDialogProps['valueValidator'],
    undefined
>;

const CreateOrUpdateDialog: React.FC<CreateOrUpdateDialogProps> = ({
    open,
    itemId,
    initialValue,
    labels,
    valueValidator,
    onSave,
    onClose,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState('');

    useEffect(() => {
        if (!open) {
            return;
        }

        setValue(initialValue || '');
    }, [initialValue, open]);

    const validationMessage = useMemo<string | null>(() => {
        return open && valueValidator ? valueValidator(value, itemId) : null;
    }, [open, itemId, value, valueValidator]);

    const isValid = open && !validationMessage;

    return (
        <Dialog open={open}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                                onClick={onClose}
                            />
                        }
                    >
                        {labels.title}
                    </DialogTitle>

                    <DialogContent>
                        <Field
                            label={labels.value}
                            required
                            validationState={
                                validationMessage ? 'error' : undefined
                            }
                            validationMessage={validationMessage || undefined}
                        >
                            <Input
                                type="text"
                                value={value}
                                onChange={(_, data) => {
                                    setValue(data.value);
                                }}
                            />
                        </Field>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            appearance="primary"
                            disabled={!isValid}
                            onClick={(e) => {
                                if (isValid && onSave) {
                                    onSave(itemId, value);
                                }
                            }}
                        >
                            {t('Button_Common_Save') }
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default CreateOrUpdateDialog;
